import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_CondenseImage = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/condense-image',
			name: 'Image Condenser — Condense Photos Online | Lift',
			description:
				'Condense photos online instantly. No watermark. Upload photo, reduce image file size while maintaining quality with our online image condenser. Try now!',
			url: 'https://liftapp.ai/condense-image',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/condense-image#product' },
				{ '@id': 'https://liftapp.ai/condense-image#howto' },
				{ '@id': 'https://liftapp.ai/condense-image#faq' },
				{ '@id': 'https://liftapp.ai/condense-image#why-lift' },
				{ '@id': 'https://liftapp.ai/condense-image#explore-tools' },
				{ '@id': 'https://liftapp.ai/condense-image#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/condense-image#product',
			name: 'Image Condenser',
			description:
				'Condense images online without losing quality. Reduce photo file sizes instantly for faster uploads, better website performance, and seamless sharing.',
			url: 'https://liftapp.ai/condense-image',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/condense-image#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Image',
					text: 'Drop your image into our image condenser.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Select Desired Size',
					text: 'Choose your target condensing level to optimize images while maintaining quality.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Download Condensed Image',
					text: 'Get your optimized image instantly — smaller file size, same sharp quality, ready to use.',
				},
			],
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/condense-image#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'How does Lift Image Condenser work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Image Condenser uses advanced algorithms to reduce file sizes while maintaining visual quality. Simply upload your photo, select your desired condensing level or target size, and download your optimized image — all in seconds with no editing skills required.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will condensing reduce image quality?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Lift's smart condensing technology minimizes quality loss while significantly reducing file sizes. You can choose between different condensing levels — for web use, light condensing delivers optimal results with virtually no visible difference from the original.",
					},
				},
				{
					'@type': 'Question',
					name: 'What image formats do you support?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift supports all major image formats including JPEG/JPG, PNG, BMP, TIFF, WEBP, and HEIC. Upload any format and condense it to your desired size — whether you need images for websites, social media, or email attachments.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe — do you store my images?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes, completely safe. Your images are processed securely and automatically deleted from our servers immediately after condensing. We never store, share, or access your files — your privacy is our priority.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/condense-image#why-lift',
			name: "Why Lift's Image Condenser stands out",
			description:
				"Lift's image condenser combines smart condensing, ease of use, and privacy.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Smaller Files, Same Quality',
					description:
						'Condense images using advanced algorithms that reduce file size while preserving sharpness, clarity, and color accuracy — no visible quality loss.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Supports All Major Image Formats',
					description:
						'Condense JPEG, JPG, PNG, BMP, TIFF, WEBP, and HEIC images in one place. Perfect for photos, graphics, and web assets.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Fast & Simple Process',
					description:
						'Drop your image, choose the desired file size, and download the condensed result. No setup, no learning curve — just instant optimization.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Private & Secure',
					description:
						'No signup required, no file storage, no watermarks. Your images are processed securely and deleted immediately after download.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Online & Device-Friendly',
					description:
						'Use the image condenser directly in your browser on desktop or mobile. No software installs — condense images anytime, anywhere.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift Image Condenser',
				description:
					'How to condense file size — quality, format support, speed, privacy, and cross-platform use.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/condense-image#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/condense-image#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Image Condenser',
					item: 'https://liftapp.ai/condense-image',
				},
			],
		},
	],
};
